import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UserProfile } from '@/lib/validations/api-schemas';
import { resetServerSessionValidation } from '@/lib/auth-session-state';
import { clearSignInAutoRestoreCooldown } from '@/lib/signin-restore-coordination';
import { resetSessionRefreshCoordinationOnLogout } from '@/lib/session-refresh';
import { bumpSessionEpoch } from '@/lib/session-epoch';
import { createZustandSafeStorage } from '@/lib/safe-local-storage';

export interface AuthState {
  user: UserProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** When `token` is omitted, the current store token is kept (OAuth + /users/me flow). Pass `null` to clear it. */
  setUser: (user: UserProfile | null, token?: string | null) => void;
  /** OAuth fragment bootstrap: set JWT before persist has written localStorage */
  setSessionToken: (token: string | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
}

/** True after Zustand has rehydrated `auth-storage` from localStorage. */
export function useAuthStoreHydrated(): boolean {
  const [hydrated, setHydrated] = useState(() => useAuth.persist.hasHydrated());

  useEffect(() => {
    if (useAuth.persist.hasHydrated()) {
      setHydrated(true);
      return;
    }
    return useAuth.persist.onFinishHydration(() => {
      setHydrated(true);
    });
  }, []);

  return hydrated;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      setUser: (user, token) =>
        set((state) => ({
          user,
          token: user === null ? null : token !== undefined ? token : state.token,
          isAuthenticated: !!user,
          error: null,
        })),
      setSessionToken: (token) => set({ token }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      logout: () => {
        bumpSessionEpoch();
        resetServerSessionValidation();
        resetSessionRefreshCoordinationOnLogout();
        clearSignInAutoRestoreCooldown();
        set({ user: null, token: null, isAuthenticated: false, error: null });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => createZustandSafeStorage() as unknown as Storage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      // Rehydration runs after main.tsx bootstrap; default merge would let persisted token=null
      // overwrite the OAuth token just set from ?access_token=.
      merge: (persisted, current) => {
        const p = persisted as Partial<AuthState> | undefined;
        return {
          ...current,
          ...p,
          token: current.token ?? p?.token ?? null,
        };
      },
    }
  )
);
